import React, { useEffect, useRef, useState } from "react";
import { X, Save, Upload, Trash2, Globe, IdCard, Timer, Users, Building2, DollarSign, Plus, Info, Pencil, ShieldCheck, AlertTriangle } from "lucide-react";
import { api, apiErrorMessage } from "../../api";
import AuthedAvatar from "./AuthedAvatar";

// Common IANA timezones — trimmed list keeps the dropdown quick to scan.
const TIMEZONES = [
  "Australia/Brisbane", "Australia/Sydney", "Australia/Melbourne", "Australia/Perth",
  "Pacific/Auckland", "Asia/Singapore", "Asia/Tokyo", "Asia/Hong_Kong", "Asia/Kolkata",
  "Asia/Dubai", "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Amsterdam",
  "Africa/Johannesburg", "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "America/Sao_Paulo", "UTC",
];

/**
 * Slide-in drawer to edit a workspace member's profile.
 * Props:
 *   open, member, workspaceId, canEdit, emailReadOnly
 *   onClose, onSaved(member)
 */
export default function MemberProfileDrawer({
  open, member, workspaceId, canEdit, currentUserId,
  emailReadOnly = true, onClose, onSaved, toast,
}) {
  const [tab, setTab] = useState("basic");
  const [form, setForm] = useState(makeForm(member));
  const [roleTags, setRoleTags] = useState([]);
  const [deptTags, setDeptTags] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef();
  const [avatarBump, setAvatarBump] = useState(0);

  useEffect(() => { setForm(makeForm(member)); setErr(""); setTab("basic"); }, [member?.user_id]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [r, d] = await Promise.all([
        api.roleTags(false).catch(() => []),
        api.deptTags(false).catch(() => []),
      ]);
      setRoleTags(r); setDeptTags(d);
    })();
  }, [open]);

  if (!open || !member) return null;

  const toggle = (key, value) => {
    setForm((f) => {
      const set = new Set(f[key]);
      if (set.has(value)) set.delete(value); else set.add(value);
      return { ...f, [key]: Array.from(set) };
    });
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const updated = await api.patchMemberProfile(workspaceId, member.user_id, {
        first_name: form.first_name,
        last_name: form.last_name,
        employee_id: form.employee_id,
        timezone: form.timezone,
        capacity_hours: Number(form.capacity_hours),
        weekly_target_hours: form.weekly_target_hours === "" ? null : Number(form.weekly_target_hours),
        roles: form.roles,
        departments: form.departments,
      });
      onSaved?.(updated);
      toast?.({ title: "Profile updated" });
      onClose?.();
    } catch (ex) {
      setErr(apiErrorMessage(ex, "Couldn't save profile"));
    } finally { setBusy(false); }
  };

  const pickPhoto = () => fileRef.current?.click();
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const updated = await api.uploadAvatar(workspaceId, member.user_id, file);
      onSaved?.(updated);
      setAvatarBump((n) => n + 1);
      toast?.({ title: "Photo updated" });
    } catch (ex) {
      setErr(apiErrorMessage(ex, "Upload failed"));
    } finally { setBusy(false); e.target.value = ""; }
  };
  const removePhoto = async () => {
    if (!member.photo_url) return;
    if (!window.confirm("Remove this photo?")) return;
    setBusy(true);
    try {
      const updated = await api.removeAvatar(workspaceId, member.user_id);
      onSaved?.(updated);
      setAvatarBump((n) => n + 1);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="member-profile-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-lg bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-cream px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3 min-w-0">
            <AuthedAvatar key={avatarBump} userId={member.user_id} name={member.name} size={48} />
            <div className="min-w-0">
              <div className="font-display text-xl truncate">{form.first_name || member.name || "—"} {form.last_name || ""}</div>
              <div className="text-xs text-[#6a736f] truncate">{member.email}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-[#6a736f]" data-testid="close-profile-drawer"><X size={20} /></button>
        </div>

        {/* Tab switcher */}
        <div className="border-b border-cream px-6 flex items-center gap-4 text-sm bg-white sticky top-[73px] z-10">
          {[
            { k: "basic", label: "Basic info" },
            { k: "rates", label: "Rates" },
            { k: "projects", label: "Assigned projects" },
            ...(canEdit ? [{ k: "permissions", label: "Permissions" }] : []),
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`profile-tab-${t.k}`}
              className={`py-3 -mb-px border-b-2 ${tab === t.k ? "border-coral text-coral font-semibold" : "border-transparent text-[#6a736f] hover:text-forest"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "rates" ? (
          <RatesPanel
            workspaceId={workspaceId}
            userId={member.user_id}
            canEdit={canEdit}
            toast={toast}
          />
        ) : tab === "projects" ? (
          <AssignedProjectsPanel
            workspaceId={workspaceId}
            userId={member.user_id}
            canEdit={canEdit}
            toast={toast}
          />
        ) : tab === "permissions" ? (
          <PermissionsPanel
            workspaceId={workspaceId}
            member={member}
            canEdit={canEdit}
            currentUserId={currentUserId}
            onSaved={onSaved}
            toast={toast}
          />
        ) : (
        <div className="p-6 space-y-6">
          {/* Photo */}
          <Section title="Photo" hint="Suggested 400×400, PNG/JPG/WEBP, up to 5 MB.">
            <div className="flex items-center gap-3">
              <AuthedAvatar key={`b${avatarBump}`} userId={member.user_id} name={member.name} size={72} />
              {canEdit && (
                <div className="flex gap-2">
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadPhoto}
                         data-testid="avatar-file-input" />
                  <button onClick={pickPhoto} disabled={busy} data-testid="upload-avatar-btn"
                          className="btn-outline-forest inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium">
                    <Upload size={12} /> Upload
                  </button>
                  {member.photo_url && (
                    <button onClick={removePhoto} disabled={busy} data-testid="remove-avatar-btn"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-[#6a736f] hover:text-red-600">
                      <Trash2 size={12} /> Remove
                    </button>
                  )}
                </div>
              )}
            </div>
          </Section>

          {/* Basic info */}
          <Section title="Basic info">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="First name">
                <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                       disabled={!canEdit} data-testid="profile-first-name"
                       className="input" />
              </Field>
              <Field label="Last name">
                <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                       disabled={!canEdit} data-testid="profile-last-name"
                       className="input" />
              </Field>
              <Field label="Work email" hint={emailReadOnly ? "Members change this from their own Settings." : ""}>
                <input value={member.email} disabled data-testid="profile-email"
                       className="input bg-cream-2/40 text-[#6a736f]" />
              </Field>
              <Field label="Employee ID" hint="Optional unique identifier within your organization.">
                <div className="relative">
                  <IdCard size={14} className="absolute left-3 top-3 text-[#6a736f]" />
                  <input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                         disabled={!canEdit} placeholder="e.g. EMP-001" data-testid="profile-employee-id"
                         className="input pl-8" />
                </div>
              </Field>
              <Field label="Timezone">
                <div className="relative">
                  <Globe size={14} className="absolute left-3 top-3 text-[#6a736f]" />
                  <select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                          disabled={!canEdit} data-testid="profile-timezone"
                          className="input pl-8 bg-white">
                    <option value="">—</option>
                    {TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
              </Field>
              <Field label="Capacity" hint="Hours per week this person is available to work.">
                <div className="relative">
                  <Timer size={14} className="absolute left-3 top-3 text-[#6a736f]" />
                  <input type="number" min={0} max={168} step="0.5"
                         value={form.capacity_hours}
                         onChange={(e) => setForm({ ...form, capacity_hours: e.target.value })}
                         disabled={!canEdit} data-testid="profile-capacity"
                         className="input pl-8" />
                </div>
              </Field>
              <Field label="Weekly target (chip)" hint="Drives the Timesheet progress chip. Leave blank to use capacity.">
                <input type="number" min={0} max={168} step="0.5"
                       value={form.weekly_target_hours ?? ""}
                       onChange={(e) => setForm({ ...form, weekly_target_hours: e.target.value })}
                       disabled={!canEdit} data-testid="profile-weekly-target"
                       className="input" />
              </Field>
            </div>
          </Section>

          {/* Role tags */}
          <Section title="Roles" hint="How you'd describe this person — Designer, Senior, PM, etc. Manage the list from the Team page.">
            <TagPicker
              tags={roleTags} selected={form.roles} onToggle={(id) => toggle("roles", id)}
              disabled={!canEdit} icon={Users} testid="profile-roles"
            />
          </Section>

          {/* Department tags */}
          <Section title="Departments" hint="Departments this person belongs to. People can be in multiple departments.">
            <TagPicker
              tags={deptTags} selected={form.departments} onToggle={(id) => toggle("departments", id)}
              disabled={!canEdit} icon={Building2} testid="profile-departments"
            />
          </Section>

          {err && <div className="text-sm text-red-600" data-testid="profile-error">{err}</div>}

          {canEdit && (
            <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-white border-t border-cream flex justify-end gap-2">
              <button onClick={onClose} className="btn-outline-forest px-4 py-2 rounded-md text-sm">Cancel</button>
              <button onClick={save} disabled={busy}
                      data-testid="save-profile-drawer"
                      className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40">
                <Save size={14} /> {busy ? "Saving…" : "Update info"}
              </button>
            </div>
          )}
        </div>
        )}
      </aside>
      <style>{`.input { width: 100%; border: 1px solid #EAF3EE; border-radius: 6px; padding: 8px 12px; font-size: 14px; }`}</style>
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div>
      <div className="font-semibold">{title}</div>
      {hint && <div className="text-xs text-[#6a736f] mt-0.5">{hint}</div>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block text-sm">
      <span className="text-xs font-semibold text-[#3f4a44]">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10px] text-[#6a736f] mt-1">{hint}</div>}
    </label>
  );
}

function TagPicker({ tags, selected, onToggle, disabled, icon: Icon, testid }) {
  if (tags.length === 0) {
    return (
      <div className="text-xs text-[#6a736f]" data-testid={`${testid}-empty`}>
        None defined yet. Create some from the Team page (top-right "Manage roles/departments").
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {tags.map((t) => {
        const on = selected.includes(t.id);
        return (
          <button
            key={t.id} type="button"
            disabled={disabled}
            onClick={() => onToggle(t.id)}
            data-testid={`${testid}-tag-${t.id}`}
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs border ${
              on ? "bg-coral text-white border-coral" : "bg-white text-forest border-cream hover:border-forest/40"
            } disabled:opacity-40`}
          >
            <Icon size={12} /> {t.name}
          </button>
        );
      })}
    </div>
  );
}

function makeForm(member) {
  return {
    first_name: member?.first_name || (member?.name || "").split(" ")[0] || "",
    last_name: member?.last_name || (member?.name || "").split(" ").slice(1).join(" ") || "",
    employee_id: member?.employee_id || "",
    timezone: member?.timezone || "",
    capacity_hours: member?.capacity_hours ?? 40,
    weekly_target_hours: member?.weekly_target_hours ?? "",
    roles: member?.roles || [],
    departments: member?.departments || [],
  };
}


// -------------------------------------------------------------------------
// Rates panel — Billable rates + Cost rates history, each row with Edit/Delete
// -------------------------------------------------------------------------
function RatesPanel({ workspaceId, userId, canEdit, toast }) {
  const [rates, setRates] = useState({ billable: [], cost: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { kind, rate } | null; rate=null → new

  const load = async () => {
    setLoading(true);
    try { setRates(await api.memberRates(workspaceId, userId)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspaceId, userId]);

  const del = async (rate) => {
    if (!window.confirm(`Delete this ${rate.kind} rate ($${rate.amount})? Cannot be undone.`)) return;
    await api.deleteMemberRate(workspaceId, userId, rate.id);
    await load();
    toast?.({ title: "Rate deleted" });
  };

  return (
    <div className="p-6 space-y-8" data-testid="rates-panel">
      <RateSection
        title="Billable rates"
        blurb={
          <>The rate you bill clients for this person's time.{" "}
          <b>We'll apply these rates to all projects that use their default rate.</b>{" "}
          You can override this rate on each project.</>
        }
        rows={rates.billable}
        loading={loading}
        canEdit={canEdit}
        onAdd={() => setEditing({ kind: "billable", rate: null })}
        onEdit={(r) => setEditing({ kind: "billable", rate: r })}
        onDelete={del}
        testid="billable"
      />
      <RateSection
        title="Cost rates"
        blurb="The internal cost this person incurs on your business. Cost rates apply to all projects."
        rows={rates.cost}
        loading={loading}
        canEdit={canEdit}
        onAdd={() => setEditing({ kind: "cost", rate: null })}
        onEdit={(r) => setEditing({ kind: "cost", rate: r })}
        onDelete={del}
        testid="cost"
      />

      {editing && (
        <RateFormModal
          workspaceId={workspaceId}
          userId={userId}
          kind={editing.kind}
          rate={editing.rate}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); toast?.({ title: "Rate saved" }); }}
        />
      )}
    </div>
  );
}

function RateSection({ title, blurb, rows, loading, canEdit, onAdd, onEdit, onDelete, testid }) {
  return (
    <div data-testid={`rate-section-${testid}`}>
      <h3 className="font-display text-xl">{title}</h3>
      <p className="text-xs text-[#3f4a44] mt-1 max-w-xl">{blurb}</p>

      {canEdit && (
        <button
          onClick={onAdd}
          data-testid={`add-${testid}-rate`}
          className="mt-3 btn-coral inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium"
        >
          <Plus size={14} /> New {testid} rate
        </button>
      )}

      <div className="mt-4 bg-white border border-cream rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream-2/50 text-xs text-[#6a736f]">
            <tr className="text-left">
              <th className="px-4 py-2">Hourly rate</th>
              <th className="px-4 py-2">Start date</th>
              <th className="px-4 py-2">End date</th>
              {canEdit && <th className="px-4 py-2 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EAF3EE]">
            {loading ? (
              <tr><td colSpan={canEdit ? 4 : 3} className="px-4 py-4 text-sm text-[#6a736f]">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={canEdit ? 4 : 3} className="px-4 py-4 text-sm text-[#6a736f]" data-testid={`${testid}-empty`}>
                <Info size={12} className="inline mr-1" />
                None yet. {canEdit ? `Add one to start tracking their ${testid} rate.` : ""}
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`${testid}-rate-row-${r.id}`}>
                <td className="px-4 py-3 font-medium tabular-nums">
                  ${r.amount.toFixed(2)}
                </td>
                <td className="px-4 py-3">
                  {r.is_first
                    ? <span className="text-[#6a736f]">All prior</span>
                    : new Date(`${r.start_date}T00:00:00`).toLocaleDateString()}
                </td>
                <td className="px-4 py-3">
                  {r.end_date
                    ? new Date(`${r.end_date}T00:00:00`).toLocaleDateString()
                    : <span className="text-[#6a736f]">All future</span>}
                </td>
                {canEdit && (
                  <td className="px-4 py-3 text-right space-x-2">
                    <button onClick={() => onEdit(r)}
                            data-testid={`edit-${testid}-rate-${r.id}`}
                            className="text-forest hover:text-coral inline-flex items-center gap-1 text-xs">
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={() => onDelete(r)}
                            data-testid={`delete-${testid}-rate-${r.id}`}
                            className="text-[#6a736f] hover:text-red-600 inline-flex items-center gap-1 text-xs">
                      <Trash2 size={12} /> Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RateFormModal({ workspaceId, userId, kind, rate, onClose, onSaved }) {
  const isEdit = !!rate;
  const [amount, setAmount] = useState(rate?.amount ?? "");
  const [startDate, setStartDate] = useState(rate?.start_date || new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (isEdit) {
        await api.updateMemberRate(workspaceId, userId, rate.id, {
          amount: Number(amount), start_date: startDate,
        });
      } else {
        await api.createMemberRate(workspaceId, userId, {
          kind, amount: Number(amount), start_date: startDate,
        });
      }
      await onSaved?.();
    } catch (ex) {
      setErr(apiErrorMessage(ex, "Couldn't save rate."));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white rounded-xl w-full max-w-sm p-6 space-y-4"
            data-testid={`rate-form-${kind}`}>
        <div className="flex items-center gap-2">
          <DollarSign size={18} className="text-coral" />
          <h3 className="font-display text-xl">
            {isEdit ? "Edit" : "New"} {kind} rate
          </h3>
        </div>
        <label className="block text-sm">
          <span className="text-xs font-semibold text-[#3f4a44]">Hourly rate ($)</span>
          <input type="number" min={0} step="0.01" required
                 value={amount} onChange={(e) => setAmount(e.target.value)}
                 autoFocus data-testid={`rate-amount-${kind}`}
                 className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
        </label>
        <label className="block text-sm">
          <span className="text-xs font-semibold text-[#3f4a44]">Effective from</span>
          <input type="date" required value={startDate}
                 onChange={(e) => setStartDate(e.target.value)}
                 data-testid={`rate-start-${kind}`}
                 className="mt-1 w-full border border-cream rounded-md px-3 py-2 text-sm" />
        </label>
        {err && <div className="text-xs text-red-600" data-testid={`rate-error-${kind}`}>{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="btn-outline-forest px-3 py-2 rounded-md text-sm">Cancel</button>
          <button type="submit" disabled={busy}
                  data-testid={`save-rate-${kind}`}
                  className="btn-coral inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40">
            <Save size={14} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}


// -------------------------------------------------------------------------
// Assigned Projects panel — Harvest-style: banner when person is on every
// project, list grouped by client, per-row X + "Manages this project" toggle
// -------------------------------------------------------------------------
function AssignedProjectsPanel({ workspaceId, userId, canEdit, toast }) {
  const [state, setState] = useState({ auto_assign_to_new_projects: false, is_assigned_to_all: false, projects: [] });
  const [available, setAvailable] = useState([]); // projects not yet assigned
  const [addSel, setAddSel] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, allProjects, allClients] = await Promise.all([
        api.memberAssignments(workspaceId, userId),
        api.projects(),
        api.clients(),
      ]);
      // Enrich each assignment row with client info if server missed it (defensive)
      const clientById = Object.fromEntries((allClients || []).map((c) => [c.id, c]));
      const enriched = { ...a, projects: a.projects.map((p) => ({
        ...p,
        client_name: p.client_name || clientById[p.client_id]?.name || "Unassigned",
      })) };
      setState(enriched);
      const assignedIds = new Set(a.projects.map((p) => p.project_id));
      setAvailable((allProjects || []).filter((p) => !assignedIds.has(p.id)));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspaceId, userId]);

  const attach = async () => {
    if (!addSel) return;
    setBusy(true);
    try { await api.assignProject(workspaceId, userId, { project_id: addSel }); setAddSel(""); await load(); }
    catch (ex) { toast?.({ title: "Couldn't attach", description: apiErrorMessage(ex, "") }); }
    finally { setBusy(false); }
  };
  const detach = async (row) => {
    if (!window.confirm(`Remove "${row.project_name}" from ${row.client_name || "the assignments"}?`)) return;
    await api.unassignProject(workspaceId, userId, row.id); await load();
  };
  const toggleManager = async (row) => {
    await api.patchAssignment(workspaceId, userId, row.id, { is_manager: !row.is_manager });
    await load();
  };
  const bulkAssignAll = async () => {
    setBusy(true);
    try { await api.bulkAssignAll(workspaceId, userId); await load(); toast?.({ title: "Assigned to every project" }); }
    finally { setBusy(false); }
  };
  const bulkUnassignAll = async () => {
    if (!window.confirm("Remove this person from every project?")) return;
    setBusy(true);
    try { await api.bulkUnassignAll(workspaceId, userId); await load(); toast?.({ title: "Removed from every project" }); }
    finally { setBusy(false); }
  };
  const toggleAutoAssign = async () => {
    await api.patchAssignmentSettings(workspaceId, userId, {
      auto_assign_to_new_projects: !state.auto_assign_to_new_projects,
    });
    await load();
  };
  const managesCount = state.projects.filter((p) => p.is_manager).length;
  const setManagesAll = async (on) => {
    setBusy(true);
    try {
      await Promise.all(state.projects
        .filter((p) => !!p.is_manager !== on)
        .map((p) => api.patchAssignment(workspaceId, userId, p.id, { is_manager: on })));
      await load();
    } finally { setBusy(false); }
  };

  // Group by client for the table
  const byClient = state.projects.reduce((acc, p) => {
    const key = p.client_name || "Unassigned";
    (acc[key] = acc[key] || []).push(p);
    return acc;
  }, {});
  const groups = Object.entries(byClient).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="p-6 space-y-5" data-testid="assignments-panel">
      <div>
        <h3 className="font-display text-xl">Your assigned projects</h3>
        <p className="text-xs text-[#3f4a44] mt-1 max-w-xl">
          As an administrator, this person can access all projects, but can only
          track time and expenses to the projects they're assigned to.
        </p>
      </div>

      {/* Info banner when assigned-to-all */}
      {state.is_assigned_to_all && (
        <div className="bg-cream-2/60 border border-cream rounded-lg p-4" data-testid="assigned-all-banner">
          <div className="text-sm font-semibold text-forest">You're assigned to all projects.</div>
          {canEdit && (
            <button
              onClick={toggleAutoAssign}
              data-testid="toggle-auto-assign"
              className="mt-1 text-xs text-coral hover:underline"
            >
              {state.auto_assign_to_new_projects
                ? "Turn off — don't auto-add to future projects"
                : "Assign to all future projects, too"}
            </button>
          )}
        </div>
      )}

      {/* Attach a project (only if not already on all) */}
      {canEdit && !state.is_assigned_to_all && (
        <div className="border border-cream rounded-lg p-3 bg-cream-2/40 flex items-center gap-2 flex-wrap">
          <select
            value={addSel} onChange={(e) => setAddSel(e.target.value)}
            data-testid="assign-project-select"
            className="flex-1 min-w-[220px] border border-cream rounded-md px-3 py-2 text-sm bg-white"
          >
            <option value="">Assign to a project…</option>
            {available.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <button
            onClick={attach} disabled={!addSel || busy}
            data-testid="assign-project-btn"
            className="btn-coral inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium disabled:opacity-40"
          >
            <Plus size={14} /> Assign
          </button>
          {available.length > 0 && (
            <button
              onClick={bulkAssignAll} disabled={busy}
              data-testid="assign-all-projects-btn"
              className="btn-outline-forest px-3 py-2 rounded-md text-xs font-medium disabled:opacity-40"
            >
              Assign to all
            </button>
          )}
        </div>
      )}

      {/* Assigned list */}
      <div className="bg-white border border-cream rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-cream bg-cream-2/40">
          <div className="text-sm font-semibold" data-testid="assigned-count">
            Assigned projects — {state.projects.length}
          </div>
          <div className="flex items-center gap-3 text-xs">
            {canEdit && state.projects.length > 0 && (
              <button onClick={bulkUnassignAll}
                      data-testid="remove-from-all-btn"
                      className="text-coral hover:underline">
                Remove from all projects
              </button>
            )}
            {canEdit && state.projects.length > 0 && (
              <div className="flex items-center gap-1 text-[#6a736f]">
                <span>Manages this project? Select</span>
                <button onClick={() => setManagesAll(true)}
                        disabled={busy || managesCount === state.projects.length}
                        data-testid="managers-select-all"
                        className="text-coral hover:underline disabled:opacity-40">All</button>
                <span>/</span>
                <button onClick={() => setManagesAll(false)}
                        disabled={busy || managesCount === 0}
                        data-testid="managers-select-none"
                        className="text-coral hover:underline disabled:opacity-40">None</button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-[#6a736f]">Loading…</div>
        ) : state.projects.length === 0 ? (
          <div className="p-6 text-sm text-[#6a736f] text-center" data-testid="assignments-empty">
            <Info size={12} className="inline mr-1" />
            {canEdit ? "Not yet assigned to any projects. Pick one above." : "No project assignments."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {groups.map(([clientName, rows]) => (
                <React.Fragment key={clientName}>
                  <tr className="bg-cream-2/30" data-testid={`client-header-${clientName}`}>
                    <td className="px-4 py-2 font-semibold text-forest" colSpan={2}>{clientName}</td>
                    <td className="px-4 py-2"></td>
                  </tr>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-t border-cream/60" data-testid={`assignment-row-${row.id}`}>
                      <td className="px-4 py-2 w-10">
                        {canEdit && (
                          <button onClick={() => detach(row)}
                                  data-testid={`detach-assignment-${row.id}`}
                                  className="text-[#6a736f] hover:text-red-600"
                                  title="Remove">
                            <X size={14} />
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {row.project_code && (
                          <span className="text-[10px] text-[#6a736f] mr-1">[{row.project_code}]</span>
                        )}
                        <span className="text-forest">{row.project_name}</span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={!!row.is_manager}
                                 onChange={() => toggleManager(row)}
                                 disabled={!canEdit}
                                 data-testid={`manages-toggle-${row.id}`}
                                 className="h-4 w-4 accent-coral" />
                        </label>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


// -------------------------------------------------------------------------
// Permissions panel — Harvest-style radio list of profiles
// Only visible to admins/owner. Owner is locked; you can't demote yourself.
// -------------------------------------------------------------------------
const PROFILES = [
  {
    id: "member",
    label: "Member",
    desc: "Track time and expenses on assigned projects.",
  },
  {
    id: "project_manager",
    label: "Project Manager",
    desc: "Manage projects, clients, and tasks. View and edit time and expenses for their team. No access to rates, invoices, people, or account settings.",
  },
  {
    id: "people_admin",
    label: "People Admin",
    desc: "Manage people and all time entries and expenses across the account. No access to rates, invoices, or account settings.",
  },
  {
    id: "accounting",
    label: "Accounting",
    desc: "Manage invoices, estimates, expenses, and clients. View rates and reports. No access to people or account settings.",
  },
  {
    id: "executive_manager",
    label: "Executive Manager",
    desc: "Manage time, expenses, projects, people, invoices, and reports. View rates. No access to account settings or billing.",
  },
  {
    id: "administrator",
    label: "Administrator",
    desc: "Full access to everything: account settings, billing, all permissions.",
  },
];

function PermissionsPanel({ workspaceId, member, canEdit, currentUserId, onSaved, toast }) {
  const [profile, setProfile] = useState(member?.permission_profile || "member");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setProfile(member?.permission_profile || "member");
    setErr("");
  }, [member?.user_id, member?.permission_profile]);

  const isOwner = member?.role === "owner";
  const isSelf = currentUserId && member?.user_id === currentUserId;
  const locked = isOwner || isSelf || !canEdit;
  const changed = profile !== (member?.permission_profile || "member");
  const showAdminHint = profile === "administrator" && !locked;

  const save = async () => {
    if (!changed) return;
    setBusy(true); setErr("");
    try {
      const updated = await api.patchMemberPermissions(workspaceId, member.user_id, {
        permission_profile: profile,
      });
      onSaved?.(updated);
      toast?.({ title: "Permissions updated" });
    } catch (ex) {
      setErr(apiErrorMessage(ex, "Couldn't update permissions."));
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 space-y-5" data-testid="permissions-panel">
      <div>
        <h3 className="font-display text-xl">
          {isSelf ? "Your permissions" : `${member?.name || "This person"}'s permissions`}
        </h3>
        <p className="text-xs text-[#3f4a44] mt-1 max-w-xl">
          This setting determines what {isSelf ? "you" : "they"} can see and do in the account.
        </p>
      </div>

      {isOwner && (
        <div className="border border-[#F5D77A] bg-[#FFF8E1] rounded-lg p-3 flex items-start gap-2"
             data-testid="permissions-owner-banner">
          <AlertTriangle size={16} className="text-[#B8860B] mt-0.5" />
          <div className="text-sm text-[#5A4A00]">
            {isSelf ? "You are the Account Owner." : `${member.name || "This person"} is the Account Owner.`}
            {" "}Owner permissions can't be changed.
          </div>
        </div>
      )}

      {!isOwner && isSelf && (
        <div className="border border-[#F5D77A] bg-[#FFF8E1] rounded-lg p-3 flex items-start gap-2"
             data-testid="permissions-self-banner">
          <AlertTriangle size={16} className="text-[#B8860B] mt-0.5" />
          <div className="text-sm text-[#5A4A00]">
            You can't change your own permissions. Ask another admin to update them.
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center gap-1 text-sm font-semibold text-[#3f4a44] mb-3">
          Profiles <Info size={12} className="text-[#6a736f]" />
        </div>
        <div className="space-y-4">
          {PROFILES.map((p) => {
            const on = profile === p.id;
            return (
              <label
                key={p.id}
                className={`flex items-start gap-3 cursor-pointer ${locked ? "opacity-70 cursor-not-allowed" : ""}`}
                data-testid={`permission-profile-${p.id}`}
              >
                <input
                  type="radio"
                  name="permission_profile"
                  value={p.id}
                  checked={on}
                  disabled={locked}
                  onChange={() => setProfile(p.id)}
                  data-testid={`permission-radio-${p.id}`}
                  className="mt-1 h-4 w-4 accent-coral"
                />
                <div className="min-w-0">
                  <div className={`text-sm font-semibold ${on ? "text-coral" : "text-forest"}`}>
                    <ShieldCheck size={12} className="inline mr-1 -mt-0.5" />
                    {p.label}
                  </div>
                  <div className="text-xs text-[#3f4a44] mt-0.5 max-w-xl leading-relaxed">{p.desc}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {showAdminHint && (
        <div className="border border-[#F5D77A] bg-[#FFF8E1] rounded-lg p-3 flex items-start gap-2"
             data-testid="permissions-admin-banner">
          <AlertTriangle size={16} className="text-[#B8860B] mt-0.5" />
          <div className="text-sm text-[#5A4A00]">
            Administrators have unrestricted access to all permissions. To limit access,
            select a different profile.
          </div>
        </div>
      )}

      {err && <div className="text-sm text-red-600" data-testid="permissions-error">{err}</div>}

      {!locked && (
        <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-white border-t border-cream flex justify-end gap-2">
          <button
            onClick={save}
            disabled={!changed || busy}
            data-testid="save-permissions"
            className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium disabled:opacity-40"
          >
            <Save size={14} /> {busy ? "Saving…" : "Update permissions"}
          </button>
        </div>
      )}
    </div>
  );
}
